import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Star, Bookmark, BookmarkCheck, Zap, ThumbsUp, ArrowUpRight } from "lucide-react";
import { aiToolsData } from "../data/aiTools";
import { getRealUrl, getRealIcon, getRealDescription, getRealPricing } from "../utils/aiToolsHelpers";
import "../styles/aiTools.css";

const API_BASE = process.env.REACT_APP_API_BASE || 
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" 
    ? "http://localhost:5005/api" 
    : "https://vblogai-backend.onrender.com/api");

const getToolRating = (toolName) => {
  let sum = 0;
  for (let i = 0; i < toolName.length; i++) {
    sum += toolName.charCodeAt(i);
  }
  const rating = 4.3 + (sum % 7) / 10;
  return rating.toFixed(1);
};

// Category emoji map
const CATEGORY_EMOJI = {
  "Latest AI": "🚀",
  "Top Trending AI": "🔥",
  "Image Generation": "🎨",
  "Video AI": "🎬",
  "Audio & Music": "🎵",
  "Developer Tools": "🛠️",
  "LLM & Chat": "🤖",
  "Productivity": "⚡",
  "No-Code": "🧩",
  "Design": "✏️",
  "Marketing": "📈",
  "Education": "🎓",
};

// getRealUrl is imported from helpers

export default function AiToolsCards({ searchQuery = "", selectedCategory = "All", selectedPricing = "All", sortBy = "default", onCountChange }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saved, setSaved]           = useState({});
  const [votes, setVotes]           = useState(() => {
    const localVotes = localStorage.getItem("vblog_votes");
    return localVotes ? JSON.parse(localVotes) : {};
  });

  useEffect(() => { fetchTools(); }, []);

  const fetchTools = async () => {
    try {
      const response = await fetch(`${API_BASE}/ai-tools`);
      if (!response.ok) throw new Error("Failed to fetch AI tools");
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      // Fall back to local data silently
      setCategories(aiToolsData);
    } finally {
      setLoading(false);
    }
  };

  const toggleSave = (toolName) => {
    setSaved((prev) => ({ ...prev, [toolName]: !prev[toolName] }));
  };

  const handleVote = (toolName) => {
    setVotes((prev) => {
      const currentVotes = prev[toolName] || 0;
      const newVotes = { ...prev, [toolName]: currentVotes + 1 };
      localStorage.setItem("vblog_votes", JSON.stringify(newVotes));
      return newVotes;
    });
  };

  // Flatten + filter + sort all tools
  const { featuredTool, filteredCategories, totalCount } = useMemo(() => {
    // Flatten all tools
    const allFlat = categories.flatMap((cat) =>
      cat.tools.map((t) => ({ ...t, _category: cat.category }))
    );

    // Apply filters
    const filterFn = (t) => {
      const matchSearch = !searchQuery ||
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.categories || []).some((c) => c.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchCat = selectedCategory === "All" ||
        (t.category || "").toLowerCase() === selectedCategory.toLowerCase() ||
        (t.categories || []).some((c) => c.toLowerCase().includes(selectedCategory.toLowerCase()));

      const matchPricing = selectedPricing === "All" ||
        getRealPricing(t).toLowerCase() === selectedPricing.toLowerCase();

      return matchSearch && matchCat && matchPricing;
    };

    // Sort function
    const sortFn = (a, b) => {
      if (sortBy === "az") return a.name.localeCompare(b.name);
      if (sortBy === "za") return b.name.localeCompare(a.name);
      if (sortBy === "featured") return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      if (sortBy === "free") {
        const order = { free: 0, freemium: 1, paid: 2 };
        return (order[getRealPricing(a).toLowerCase()] || 0) - (order[getRealPricing(b).toLowerCase()] || 0);
      }
      return 0;
    };

    // Pick featured hero tool (first featured + verified from first category)
    const firstFeatured = allFlat.find((t) => t.featured && t.verified);

    // Re-group filtered tools by category
    const grouped = {};
    categories.forEach((cat) => { grouped[cat.category] = []; });
    allFlat.filter(filterFn).sort(sortFn).forEach((t) => {
      if (grouped[t._category] !== undefined) {
        grouped[t._category].push(t);
      }
    });

    const result = Object.entries(grouped)
      .filter(([, tools]) => tools.length > 0)
      .map(([category, tools]) => ({ category, tools }));

    const total = result.reduce((sum, cat) => sum + cat.tools.length, 0);

    return { featuredTool: firstFeatured, filteredCategories: result, totalCount: total };
  }, [categories, searchQuery, selectedCategory, selectedPricing, sortBy]);

  // Report count up to parent
  useEffect(() => { onCountChange?.(totalCount); }, [totalCount, onCountChange]);

  // ===== LOADING =====
  if (loading) {
    return (
      <div className="skeleton-grid">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="skeleton-card">
            <div className="skel-line" style={{ height: 20, width: "60%", marginBottom: 12 }} />
            <div className="skel-line" style={{ height: 14, width: "90%" }} />
            <div className="skel-line" style={{ height: 14, width: "75%" }} />
            <div className="skel-line" style={{ height: 14, width: "50%", marginTop: 20 }} />
          </div>
        ))}
      </div>
    );
  }


  // ===== EMPTY SEARCH =====
  if (filteredCategories.length === 0) {
    return (
      <div className="tools-container">
        <div className="empty-state">
          <div style={{ fontSize: 48 }}>🔍</div>
          <h3>No tools found</h3>
          <p>Try adjusting your search or filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tools-container">

      {/* FEATURED HERO CARD - shown only on default view */}
      {featuredTool && !searchQuery && selectedCategory === "All" && selectedPricing === "All" && (
        <div className="featured-hero">
          <div className="featured-hero-content">
            <div className="featured-hero-badge">
              <Zap size={12} fill="currentColor" /> ⭐ Editor's Pick
            </div>
            <h2>{featuredTool.name}</h2>
            <p>{getRealDescription(featuredTool)}</p>
            <div className="featured-hero-tags">
              {(featuredTool.categories || []).map((cat, i) => (
                <span key={i}>{cat}</span>
              ))}
              <span>{getRealPricing(featuredTool)}</span>
            </div>
            <div className="featured-hero-actions">
              <a href={getRealUrl(featuredTool)} target="_blank" rel="noopener noreferrer" className="btn-primary">
                Visit Tool <ExternalLink size={14} />
              </a>
              <button
                className="btn-secondary"
                onClick={() => toggleSave(featuredTool.name)}
              >
                {saved[featuredTool.name] ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
                {saved[featuredTool.name] ? "Saved" : "Save"}
              </button>
            </div>
          </div>
          <div className="featured-hero-visual">
            <div className="featured-hero-icon-wrap">
              <img src={getRealIcon(featuredTool)} alt={featuredTool.name} />
            </div>
          </div>
        </div>
      )}

      {/* CATEGORIES */}
      {filteredCategories.map((catObj, catIndex) => (
        <div className="category-section" key={catIndex}>

          <div className="category-header">
            <h2 className="category-title">{catObj.category}</h2>
            <span className="category-count">{catObj.tools.length} tools</span>
            <Link to={`/category/${encodeURIComponent(catObj.category)}`} className="category-view-all">
              View all <ExternalLink size={13} />
            </Link>
          </div>

          <div className="category-grid">
            {catObj.tools.slice(0, 4).map((tool, i) => (
              <div className="tool-card" key={i} id={`tool-card-${tool.name.replace(/\s/g, "-")}`}>

                <div className="tool-card-new-header">
                  <div className="tool-brand">
                    <img
                      src={getRealIcon(tool)}
                      alt={tool.name}
                      className="tool-logo"
                      onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(tool.name)}&background=1a2b6b&color=fff&size=64`; }}
                    />
                    <h3 className="tool-title">{tool.name}</h3>
                  </div>
                  <div className="tool-votes">
                    <ThumbsUp size={14} className="vote-icon" /> 
                    <span>{votes[tool.name] || 0}</span>
                  </div>
                </div>

                <div className="tool-card-new-body">
                  <p className="tool-desc">
                    {getRealDescription(tool)}
                  </p>
                </div>

                <div className="tool-card-new-footer">
                  <span className={`badge-pricing badge-${getRealPricing(tool).toLowerCase().replace(/\s/g, "")}`}>
                    {getRealPricing(tool).toUpperCase()}
                  </span>
                  <a
                    href={getRealUrl(tool)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tool-visit-link"
                  >
                    Visit <ArrowUpRight size={14} className="visit-icon" />
                  </a>
                </div>

              </div>
            ))}
          </div>
        </div>
      ))}

    </div>
  );
}